import { camelCase, get } from 'lodash'
import type { TEntityId, IEntityDataItem, ISelectItem } from '~/types/models'
import type { TFieldSetItem } from '~/types/fieldSet/models'

export async function ensureUniquenessAndPropagateFlagChange({
  callingMn,
  sourceEntityId,
  sourceEntityFlagPropertyName,
  sourceEntityArray,
  sourceEntityData,
  sourceEntitySelectItemLabelPathString, // Set when we just want an ISelectItem, not an IEntityDataItem. We may want either the sourceEntity itself, or one of its properties.
  sourceEntitySelectItemIdPathString, // Set when we want to return a property of the sourceEntity, not the sourceEntity itself
  targetEntity,
  finallyFn = () => {},
}: {
  callingMn: string
  sourceEntityId: TEntityId
  sourceEntityFlagPropertyName: string
  sourceEntityArray: IEntityDataItem[] | TFieldSetItem[]
  sourceEntityData: Partial<IEntityDataItem>
  sourceEntitySelectItemLabelPathString?: string
  sourceEntitySelectItemIdPathString?: TEntityId
  targetEntity: IEntityDataItem | undefined
  finallyFn?: Function
}): Promise<boolean> {
  const fn =
    `${callingMn} --> [cynqueModules/ensureUniquenessAndPropagateFlagChange] |` +
    ` ${ensureUniquenessAndPropagateFlagChange.name}`
  if (!sourceEntityData.hasOwnProperty(sourceEntityFlagPropertyName)) {
    console.debug(
      `${fn} changed data does NOT include ${sourceEntityFlagPropertyName}`
    )
    return false
  }
  console.debug(
    `${fn} includes ${sourceEntityFlagPropertyName}` +
      ` (${sourceEntityData[sourceEntityFlagPropertyName]})`
  )
  if (sourceEntityData[sourceEntityFlagPropertyName] === true) {
    console.debug(
      `${fn} ${sourceEntityFlagPropertyName} in source entity is true.` +
        ` Clearing value in other objects.`
    )
    // clear flag (typically checkbox) value in other rows
    sourceEntityArray
      .filter((se: IEntityDataItem | TFieldSetItem) => se.id !== sourceEntityId)
      .forEach((se: IEntityDataItem | TFieldSetItem) => {
        se[sourceEntityFlagPropertyName] = false
      })
  }

  // reassign targetProperty (if passed a targetEntity) based on change
  if (targetEntity) {
    const targetPropertyName = camelCase(
      sourceEntityFlagPropertyName.replace('is', '')
    )
    const sourceEntity = sourceEntityArray.find(
      (se) => se.id === sourceEntityId
    )
    if (!sourceEntity) {
      throw new Error(
        `${fn} sourceEntity just updated can't be found. This should never happen.`
      )
    }
    console.debug(`${fn} Found sourceEntity`, sourceEntity)

    const potentialTargetPropertyValue:
      | IEntityDataItem
      | TFieldSetItem
      | undefined = sourceEntityArray.find(
      (se) => se[sourceEntityFlagPropertyName] // truthy value for checkbox property
    )
    let targetPropertyValue:
      | IEntityDataItem
      | TFieldSetItem
      | ISelectItem
      | undefined = potentialTargetPropertyValue // starting value. May get changed below.

    // if (targetPropertyValue) {
    //   if (sourceEntitySelectItemLabelPathString) {
    //     const label = get(
    //       targetPropertyValue,
    //       sourceEntitySelectItemLabelPathString
    //     )
    //     targetPropertyValue = { id: targetPropertyValue.id, label }
    //   }
    // }

    if (targetPropertyValue) {
      if (
        sourceEntitySelectItemLabelPathString &&
        sourceEntitySelectItemIdPathString
      ) {
        const label = get(
          targetPropertyValue,
          sourceEntitySelectItemLabelPathString
        )
        const id = get(targetPropertyValue, sourceEntitySelectItemIdPathString)
        targetPropertyValue = { id, label }
      } else if (sourceEntitySelectItemLabelPathString) {
        const label = get(
          targetPropertyValue,
          sourceEntitySelectItemLabelPathString
        )
        targetPropertyValue = { id: targetPropertyValue.id, label }
      } else if (sourceEntitySelectItemIdPathString) {
        const id = get(targetPropertyValue, sourceEntitySelectItemIdPathString)
        targetPropertyValue = { id, label: targetPropertyValue.label }
      }
    }

    targetEntity[targetPropertyName] = targetPropertyValue
    console.debug(
      `${fn} target entity after reassigment based on flag change:`,
      targetEntity
    )
  }
  await finallyFn()
  return true
}
